type JwtPayload = Record<string, unknown>;

const BEARER_PREFIX = "Bearer ";

export const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const rawToken = token.startsWith(BEARER_PREFIX)
      ? token.slice(BEARER_PREFIX.length)
      : token;

    // JWT segments are base64url-encoded; atob only accepts the standard alphabet.
    const payloadSegment = rawToken
      .split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    return JSON.parse(window.atob(payloadSegment));
  } catch {
    return null;
  }
};

const withoutTimestamps = ({ iat, nbf, exp, ...claims }: JwtPayload) => claims;

const serializeClaims = (claims: JwtPayload) =>
  JSON.stringify(
    Object.keys(claims)
      .toSorted()
      .map((key) => [key, claims[key]]),
  );

/**
 * Compares two JWTs by their claims, ignoring the issuance/expiry timestamps
 * (`iat`/`nbf`/`exp`). The backend re-mints the token on every user-info call,
 * so tokens that differ only in timestamps are semantically the same session.
 */
export const areJwtClaimsEqual = (
  a: string | null,
  b: string | null,
): boolean => {
  const payloadA = a ? decodeJwtPayload(a) : null;
  const payloadB = b ? decodeJwtPayload(b) : null;

  if (!payloadA || !payloadB) {
    return false;
  }

  return (
    serializeClaims(withoutTimestamps(payloadA)) ===
    serializeClaims(withoutTimestamps(payloadB))
  );
};

/**
 * True when both tokens belong to the same user acting in the same identity
 * context (`nameid` + `identity_user_id`; the latter is undefined for
 * non-migrated users on both sides, which counts as a match).
 */
export const hasSameJwtIdentity = (a: string, b: string): boolean => {
  const payloadA = decodeJwtPayload(a);
  const payloadB = decodeJwtPayload(b);

  if (!payloadA || !payloadB) {
    return false;
  }

  return (
    payloadA.nameid === payloadB.nameid &&
    payloadA.identity_user_id === payloadB.identity_user_id
  );
};
